/* gps-pet-tracking.js — vanilla progressive enhancement for the GPS Pet Tracking page.
 * Replaces the framework runtime from the React prototype:
 *   1. the GpsDecision widget ([data-sn-widget="GpsDecision"]) — three questions →
 *      a candid verdict (GPS tracker vs smart QR tag), rebuilt on each answer.
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The page renders a faithful static snapshot server-side; this script enhances it.
 * If the script never runs, the static question/verdict shell + FAQ questions stay visible.
 */
(function () {
  'use strict';

  // ---- Decision data (ported verbatim from GpsDecision.jsx) -----------------
  // ---- Locale ------------------------------------------------------------
  // The theme sets <html lang> from request.locale.iso_code, so /de/ pages
  // report "de" here. Any non-German locale falls through to English.
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false; // guard: sn-engraved-tag.js is also run head-less by _render-engraved-tag-svg.mjs
  var T = function (en, de) { return SN_DE ? de : en; };

  var GD_QUESTIONS = SN_DE ? [
    { id: 'escaper', q: 'Ist Ihr Tier ein Arbeitshund oder ein notorischer Ausreißer?',
      opts: [{ v: 'yes', label: 'Ja' }, { v: 'no', label: 'Nein' }] },
    { id: 'remote', q: 'Sind Sie meist in abgelegenem Gelände ohne Netz unterwegs?',
      opts: [{ v: 'yes', label: 'Oft' }, { v: 'no', label: 'Selten' }] },
    { id: 'need', q: 'Wollen Sie den Standort live sehen - oder Ihr Tier einfach zurück?',
      opts: [{ v: 'live', label: 'Live-Karte' }, { v: 'home', label: 'Einfach zurück' }] },
  ] : [
    { id: 'escaper', q: 'Is your pet a working dog or a determined escaper?',
      opts: [{ v: 'yes', label: 'Yes' }, { v: 'no', label: 'No' }] },
    { id: 'remote', q: 'Do you mostly walk in remote, off-grid places?',
      opts: [{ v: 'yes', label: 'Often' }, { v: 'no', label: 'Rarely' }] },
    { id: 'need', q: 'Do you need to watch a live location - or just get them home?',
      opts: [{ v: 'live', label: 'Live map' }, { v: 'home', label: 'Get them home' }] },
  ];

  function gdEvaluate(a) {
    if (!a.escaper || !a.remote || !a.need) return null;
    var reasons = [];
    var gps = 0;
    if (a.escaper === 'yes') { gps++; reasons.push({ gps: true, t: T('A working dog or escaper can cover real distance before anyone spots them - live tracking earns its place.', 'Ein Arbeitshund oder Ausreißer legt echte Strecken zurück, bevor ihn jemand bemerkt - da lohnt sich Live-Ortung.') }); }
    if (a.remote === 'yes') { gps++; reasons.push({ gps: true, t: T('Out on remote ground, there may be no passer-by to find your pet - that’s the one case a scan-based tag can’t cover.', 'In abgelegenem Gelände ist womöglich niemand da, der Ihr Tier findet - das ist der eine Fall, den eine scanbasierte Marke nicht abdeckt.') }); }
    if (a.need === 'live') { gps++; reasons.push({ gps: true, t: T('You want a live dot on a map - only GPS does that.', 'Sie wollen einen Punkt live auf der Karte sehen - das kann nur GPS.') }); }
    if (a.escaper === 'no') reasons.push({ gps: false, t: T('Not a habitual escaper - most pets turn up within a mile, found by a person.', 'Kein notorischer Ausreißer - die meisten Tiere tauchen in der Nähe wieder auf und werden von Menschen gefunden.') });
    if (a.remote === 'no') reasons.push({ gps: false, t: T('Mostly populated areas - there’ll usually be someone to scan the tag.', 'Überwiegend belebte Gegenden - da ist meist jemand, der die Marke scannen kann.') });
    if (a.need === 'home') reasons.push({ gps: false, t: T('You just want them home fast - a scan does that without a battery or a bill.', 'Sie wollen Ihr Tier einfach schnell zurück - ein Scan schafft das ohne Akku und ohne laufende Kosten.') });
    return { rec: gps >= 1 ? 'gps' : 'tag', reasons: reasons };
  }

  var GD_VERDICT = {
    gps: {
      tag: T('A GPS tracker earns its place', 'Ein GPS-Tracker lohnt sich hier'), tone: '#E07A3E', bg: '#FFE3CE', fg: '#7A3B16',
      headline: T('For your situation, a live GPS tracker is the better tool.', 'In Ihrer Situation ist ein echter GPS-Tracker das bessere Werkzeug.'),
      foot: T('Just go in clear-eyed: most need charging every few days and a monthly fee. Plenty of owners add a smart tag too, so a finder can help the moment the battery’s flat.', 'Gehen Sie es mit offenen Augen an: Die meisten müssen alle paar Tage geladen werden und kosten monatlich. Viele Halter nehmen zusätzlich eine smarte Marke - damit ein Finder helfen kann, sobald der Akku leer ist.'),
      cta: T('See the smart tag anyway', 'Die smarte Marke trotzdem ansehen'),
    },
    tag: {
      tag: T('A smart tag is likely all you need', 'Eine smarte Marke genügt Ihnen wahrscheinlich'), tone: '#5B8C3E', bg: '#E6FFC6', fg: '#224031',
      headline: T("Honestly? You probably don't need GPS.", 'Ehrlich? Sie brauchen wahrscheinlich kein GPS.'),
      foot: T('A smart QR tag gets most pets home just as well - no charging, no device fee, lighter on the collar - because pets are found by people, and a scan reaches you instantly.', 'Eine smarte QR-Marke bringt die meisten Tiere genauso zuverlässig nach Hause - ohne Laden, ohne Gerätegebühr, leichter am Halsband. Denn Tiere werden von Menschen gefunden, und ein Scan erreicht Sie sofort.'),
      cta: T('Get Your Tag', 'Marke sichern'),
    },
  };

  // ---- SVG icons (ported from illustrations.jsx) ----------------------------
  var ICON_PATHS = {
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"></path>',
    check: '<path d="M20 6 9 17l-5-5"></path>',
    shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"></path><path d="m9 12 2 2 4-4"></path>',
    alert: '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"></path><path d="M12 9v4M12 17h.01"></path>',
  };
  function icon(name, size, color) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + ICON_PATHS[name] + '</svg>';
  }

  // ---- Tiny DOM helper ------------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var at in opts.attrs) e.setAttribute(at, opts.attrs[at]);
    (kids || []).forEach(function (c) {
      if (c == null) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  // ---- GpsDecision widget ---------------------------------------------------
  function initDecision(root) {
    var state = { escaper: null, remote: null, need: null };

    // Identify the two structural columns of the SSR markup.
    var grid = root.firstElementChild;            // the grid
    var leftCol = grid.children[0];               // questions column
    var verdictCol = grid.children[1];            // verdict column

    // The question rows are the children of leftCol after the heading block.
    // leftCol children: [0] heading, [1..3] question blocks.
    var questionBlocks = [];
    for (var i = 1; i < leftCol.children.length; i++) questionBlocks.push(leftCol.children[i]);

    // Wire each question's option buttons.
    questionBlocks.forEach(function (block, qi) {
      var q = GD_QUESTIONS[qi];
      var btns = block.querySelectorAll('button');
      for (var bi = 0; bi < btns.length; bi++) {
        (function (btn, opt) {
          btn.setAttribute('data-gd-q', q.id);
          btn.setAttribute('data-gd-v', opt.v);
          btn.addEventListener('click', function () {
            state[q.id] = opt.v;
            syncButtons();
            renderVerdict();
          });
        })(btns[bi], q.opts[bi]);
      }
    });

    function syncButtons() {
      questionBlocks.forEach(function (block, qi) {
        var q = GD_QUESTIONS[qi];
        var btns = block.querySelectorAll('button');
        for (var bi = 0; bi < btns.length; bi++) {
          var on = state[q.id] === q.opts[bi].v;
          var btn = btns[bi];
          btn.style.border = on ? '2px solid var(--sn-forest)' : '1.5px solid var(--sn-line)';
          btn.style.background = on ? 'var(--sn-forest)' : '#fff';
          btn.style.color = on ? 'var(--sn-cream)' : 'var(--sn-ink)';
        }
      });
    }

    function placeholderPanel() {
      return [h('div', { css: 'margin:auto;text-align:center;max-width:260px' }, [
        h('div', { css: 'width:64px;height:64px;border-radius:999px;background:rgba(255,248,240,0.1);border:1px solid rgba(255,248,240,0.2);display:flex;align-items:center;justify-content:center;margin:0 auto 16px',
          html: icon('shield', 30, 'var(--sn-lime)') }),
        h('h3', { css: 'font-size:20px;margin-bottom:8px;color:var(--sn-cream)' }, [T('Answer the three questions', 'Beantworten Sie die drei Fragen')]),
        h('p', { css: 'font-size:14px;color:rgba(255,248,240,0.72);line-height:1.55' }, [T('We’ll give you a straight recommendation - even when that’s “buy the GPS tracker.”', 'Sie bekommen eine klare Empfehlung - auch dann, wenn sie „kaufen Sie den GPS-Tracker“ lautet.')]),
      ])];
    }

    function resultPanel(result, v) {
      var eyebrow = h('span', { cls: 'sn-eyebrow', css: 'color:' + v.tone + ';margin-bottom:12px;display:inline-flex;align-items:center;gap:8px' }, [
        h('span', { css: 'width:8px;height:8px;border-radius:999px;background:' + v.tone }),
        ' ' + v.tag,
      ]);
      var headline = h('h3', { css: 'font-size:25px;line-height:1.14;margin-bottom:16px' }, [v.headline]);
      var list = h('ul', { css: 'list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:11px' },
        result.reasons.map(function (r) {
          return h('li', { css: 'display:flex;gap:10px;font-size:14.5px;line-height:1.5' }, [
            h('span', { css: 'margin-top:2px;flex-shrink:0', html: icon(r.gps ? 'alert' : 'check', 16, v.fg) }),
            h('span', {}, [r.t]),
          ]);
        }));
      var foot = h('div', { css: 'margin-top:auto;padding-top:22px' }, [
        h('p', { css: 'font-size:13.5px;line-height:1.55;opacity:0.78;margin-bottom:16px' }, [v.foot]),
        h('a', { cls: 'sn-btn sn-btn--primary', css: 'width:100%;justify-content:center' }, [
          h('span', {}, [v.cta]), ' ',
          h('span', { css: 'display:inline-flex', html: icon('arrow', 18, 'currentColor') }),
        ]),
      ]);
      return [h('div', { cls: 'sn-msg-in', css: 'display:flex;flex-direction:column;height:100%' }, [
        eyebrow, headline, list, foot,
      ])];
    }

    function renderVerdict() {
      var result = gdEvaluate(state);
      var v = result ? GD_VERDICT[result.rec] : null;
      verdictCol.style.backgroundColor = v ? v.bg : '#224031';
      verdictCol.style.color = v ? v.fg : 'var(--sn-cream)';
      verdictCol.innerHTML = '';
      (result ? resultPanel(result, v) : placeholderPanel()).forEach(function (n) { verdictCol.appendChild(n); });
    }

    syncButtons();
    renderVerdict();
  }

  // The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
  // Answers are rendered into the static body.html (for SEO/crawlability), so this
  // file only owns the GpsDecision widget.

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var page = document.getElementById('sn-gps-pet-tracking');
    if (!page) return;
    var widget = page.querySelector('[data-sn-widget="GpsDecision"]');
    if (widget) initDecision(widget);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
